import { useCart, useUpsertCartItem } from '@/api/carts/carts.hooks.ts';
import { theme } from '@/config/theme.ts';
import {
  Add,
  AddShoppingCart,
  Remove,
  RemoveShoppingCartOutlined,
} from '@mui/icons-material';
import { Stack, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { GridRenderCellParams } from '@mui/x-data-grid/models';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const RenderCartQuantity = (
  props: GridRenderCellParams<any, any, any>,
): React.ReactNode => {
  const { row, api, value } = props;
  const { t } = useTranslation('customer');
  const { data } = useCart({
    refetchOnMount: false,
  });
  const cartItems = useMemo(() => data?.cartItems || [], [data]);
  const cartItem = cartItems.find((item) => item.itemId === row.id);
  const { mutateAsync: addCartItems, isPending: loading } = useUpsertCartItem();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const updateCartQuantity = (quantity: number | undefined) => {
    api.updateRows([
      {
        id: row.id,
        cartQuantity: quantity,
      },
    ]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
      }}
    >
      {!isMobile && (
        <Stack
          sx={{
            height: '30px',
            width: 'auto',
            display: 'flex',
            alignItems: 'center',
            flexGrow: 0,
            backgroundColor: '#e1e1e1',
            borderRadius: '4px',
            my: 'auto',
          }}
          direction={'row'}
        >
          <IconButton
            size={'small'}
            onClick={() => {
              const currentValue = value === undefined ? 1 : value || 0;
              if (currentValue <= 0) return;
              updateCartQuantity(currentValue - 1);
            }}
            sx={{
              py: 0,
            }}
          >
            <Remove sx={{ height: '20px', width: '20px' }} />
          </IconButton>
          <input
            id={row.id}
            className={'no-arrows'}
            style={{
              all: 'unset',
              flexGrow: 1,
              minWidth: '25px',
              maxWidth: '25px',
              textAlign: 'center',
              backgroundColor: '#f7f7f7',
              height: '100%',
            }}
            inputMode={'numeric'}
            placeholder="1"
            value={value === undefined || value === null ? '' : value}
            onChange={(e) => {
              const newValue = e.target.value;
              // Allow empty input
              if (newValue === '') {
                updateCartQuantity(undefined);
                return;
              }
              // Only allow numbers
              if (!/^\d*$/.test(newValue)) {
                return;
              }
              const parsedValue = parseInt(newValue);
              if (!isNaN(parsedValue)) {
                updateCartQuantity(parsedValue);
              }
            }}
            onBlur={(e) => {
              const value = e.target.value;
              // If empty or invalid, set to 1
              if (value === '' || isNaN(parseInt(value))) {
                updateCartQuantity(1);
                return;
              }
              // Ensure non-negative
              const parsedValue = Math.max(0, parseInt(value));
              updateCartQuantity(parsedValue);
            }}
          />
          <IconButton
            size={'small'}
            onClick={() => {
              updateCartQuantity((value || 0) + 1);
              // cartStore.addToCart(row.itemCode, value + 1);
            }}
            sx={{
              py: 0,
            }}
          >
            <Add sx={{ height: '20px', width: '20px' }} />
          </IconButton>
        </Stack>
      )}
      <Box>
        {cartItem ? (
          <Tooltip
            disableFocusListener
            disableTouchListener
            disableInteractive
            title={t('items.cart.removeFromCart')}
          >
            <IconButton
              color={'inherit'}
              sx={{ ml: !isMobile ? 1 : 0 }}
              disabled={loading}
              onClick={() =>
                addCartItems({
                  cartItems: [{ itemId: cartItem.itemId, quantity: 0 }],
                })
              }
            >
              <RemoveShoppingCartOutlined />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip
            disableFocusListener
            disableTouchListener
            disableInteractive
            title={t('items.cart.addToCart')}
          >
            <IconButton
              color={'inherit'}
              sx={{ ml: !isMobile ? 1 : 0 }}
              disabled={loading}
              onClick={async () =>
                await addCartItems({
                  cartItems: [{ itemId: row.id, quantity: value || 1 }],
                })
              }
            >
              <AddShoppingCart />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
